/**
 * Lo que decide la pantalla del catalogo publico, sin React.
 *
 * Aca vive lo que se puede probar sin montar nada: que avisos mostrar, como se
 * mueve un rubro de lugar, que productos pasan el filtro, y como se achica una
 * foto antes de subirla.
 */
import { esUnidadUsada } from "@tora/shared";
import { colorDeIphone } from "@/components/catalogo/colores";
import type { Producto, ResumenPublicacion } from "@/lib/tipos";
import { aNumero } from "@/lib/tipos";

// ─────────────────────────────────────────────────────────────────────────────
// Los avisos
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Cada aviso lleva a donde se arregla. Un aviso que solo informa hace que la
 * persona tenga que adivinar en cual de las tres secciones buscar.
 */
export type AccionAviso = "contacto" | "sin-foto" | "sin-precio" | "ninguna";

export interface Aviso {
  clave: string;
  texto: string;
  tono: "warning" | "info";
  accion: AccionAviso;
}

/**
 * Los avisos van de lo que ROMPE el catalogo a lo que solo lo deja incompleto.
 *
 * Sin productos publicados el link no sirve para nada, asi que ese va primero.
 * Sin WhatsApp la pagina se ve pero no tiene como contactar, que es el unico
 * objetivo de publicarla. Las fotos y los precios son grados de terminado.
 */
export function avisosDePublicacion(resumen: ResumenPublicacion | undefined): Aviso[] {
  if (!resumen) return [];
  const { totales, negocio } = resumen;
  const avisos: Aviso[] = [];

  if (totales.rubros_visibles === 0) {
    avisos.push({
      clave: "sin-rubros",
      texto: "No hay ningún rubro visible: el catálogo se ve vacío.",
      tono: "warning",
      accion: "ninguna",
    });
  } else if (totales.publicados === 0) {
    avisos.push({
      clave: "sin-productos",
      texto: "Todavía no publicaste ningún producto.",
      tono: "warning",
      accion: "ninguna",
    });
  }

  if (!negocio.whatsapp?.trim()) {
    avisos.push({
      clave: "sin-whatsapp",
      texto: "Falta el WhatsApp: el catálogo no tiene botón de consulta.",
      tono: "warning",
      accion: "contacto",
    });
  }

  if (totales.sin_precio > 0) {
    avisos.push({
      clave: "sin-precio",
      texto:
        totales.sin_precio === 1
          ? "1 producto publicado no tiene precio: no se ve hasta que lo tenga."
          : `${totales.sin_precio} productos publicados no tienen precio: no se ven hasta que lo tengan.`,
      tono: "warning",
      accion: "sin-precio",
    });
  }

  if (totales.sin_foto > 0) {
    avisos.push({
      clave: "sin-foto",
      texto:
        totales.sin_foto === 1
          ? "1 producto publicado no tiene foto."
          : `${totales.sin_foto} productos publicados no tienen foto.`,
      tono: "info",
      accion: "sin-foto",
    });
  }

  return avisos;
}

/** «123 productos publicados en 14 rubros». */
export function lineaDeEstado(resumen: ResumenPublicacion | undefined): string {
  if (!resumen) return "";
  const { publicados, rubros_visibles, rubros_con_publicados } = resumen.totales;
  if (publicados === 0) return "Nada publicado todavía";
  const p = publicados === 1 ? "1 producto publicado" : `${publicados} productos publicados`;
  // Los rubros donde hay algo que ver, no los prendidos: un rubro visible sin
  // nada publicado no le aparece al cliente.
  const rubros = rubros_con_publicados ?? rubros_visibles;
  const r = rubros === 1 ? "1 rubro" : `${rubros} rubros`;
  return `${p} en ${r}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// El orden de los rubros
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Devuelve la lista con el rubro corrido un lugar. En los bordes devuelve la
 * MISMA lista (no una copia): quien llama compara por identidad para saber si
 * hay algo que mandar al backend.
 */
export function moverRubro(ids: number[], id: number, hacia: "arriba" | "abajo"): number[] {
  const i = ids.indexOf(id);
  if (i === -1) return ids;
  const j = hacia === "arriba" ? i - 1 : i + 1;
  if (j < 0 || j >= ids.length) return ids;

  const copia = [...ids];
  [copia[i], copia[j]] = [copia[j] as number, copia[i] as number];
  return copia;
}

// ─────────────────────────────────────────────────────────────────────────────
// El filtro de productos
// ─────────────────────────────────────────────────────────────────────────────

export interface FiltroPublicacion {
  texto: string;
  soloSinFoto: boolean;
  soloSinPrecio: boolean;
}

export const FILTRO_VACIO: FiltroPublicacion = {
  texto: "",
  soloSinFoto: false,
  soloSinPrecio: false,
};

export function hayFiltro(f: FiltroPublicacion): boolean {
  return f.texto.trim() !== "" || f.soloSinFoto || f.soloSinPrecio;
}

/**
 * Si un rubro muestra sus productos.
 *
 * Con un filtro puesto se abren todos para buscar adentro, pero el que no
 * tiene ninguna coincidencia vuelve a plegarse. Antes "Solo sin foto" dejaba
 * los 21 rubros abiertos, y 20 repetian «Ningún producto de este rubro
 * coincide». Abierto a mano se respeta siempre: ahi el mensaje se pidio.
 */
export function rubroDesplegado(e: {
  abiertoAMano: boolean;
  abiertoPorFiltro: boolean;
  cargando: boolean;
  coincidencias: number;
}): boolean {
  if (e.abiertoAMano) return true;
  return e.abiertoPorFiltro && (e.cargando || e.coincidencias > 0);
}

/**
 * Con un filtro puesto, los rubros que tienen algo que mostrar.
 *
 * Buscar «zzz» dejaba los 21 rubros plegados con sus contadores, igual que sin
 * filtro, y sin ningun aviso: parecia que el filtro no hacia nada. Ahora se
 * dibujan solo los que coinciden, y si no queda ninguno la pantalla lo dice.
 * Un rubro que todavia esta cargando (`null`) se deja: puede tener algo.
 */
export function rubrosConCoincidencias<R extends { id: number }>(
  rubros: readonly R[],
  filtro: FiltroPublicacion,
  productosPorRubro: ReadonlyMap<number, readonly Producto[] | null>,
): R[] {
  if (!hayFiltro(filtro)) return [...rubros];
  return rubros.filter((r) => {
    const productos = productosPorRubro.get(r.id);
    return productos == null || filtrarProductos(productos, filtro).length > 0;
  });
}

/**
 * Los tres filtros se combinan con Y.
 *
 * «Sin foto» y «sin precio» miran solo lo PUBLICADO, igual que los contadores
 * del backend: lo que no esta publicado no le falta nada, porque no se ve.
 */
export function filtrarProductos(productos: readonly Producto[], f: FiltroPublicacion): Producto[] {
  const texto = f.texto.trim().toLowerCase();
  return productos.filter((p) => {
    if (texto !== "" && !p.nombre.toLowerCase().includes(texto)) return false;
    if (f.soloSinFoto && !(p.publicado && p.imagen_url === null)) return false;
    if (f.soloSinPrecio && !(p.publicado && aNumero(p.precio_venta) <= 0)) return false;
    return true;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// La foto
// ─────────────────────────────────────────────────────────────────────────────

/** Lado mayor de una foto de producto. El logo va mas chico. */
export const LADO_PRODUCTO = 1200;
export const LADO_LOGO = 400;

export interface ImagenAchicada {
  blob: Blob;
  ancho: number;
  alto: number;
}

/**
 * Devuelve las medidas que entran en `lado` sin deformar. Nunca AGRANDA: subir
 * una foto de 300px estirada a 1200 solo hace pesar mas lo mismo.
 */
export function medidasAchicadas(
  ancho: number,
  alto: number,
  lado: number,
): { ancho: number; alto: number } {
  const mayor = Math.max(ancho, alto);
  if (mayor <= lado || mayor === 0) return { ancho, alto };
  const factor = lado / mayor;
  return {
    ancho: Math.max(1, Math.round(ancho * factor)),
    alto: Math.max(1, Math.round(alto * factor)),
  };
}

/**
 * Achica en el navegador antes de subir.
 *
 * Una foto de celular pesa 4 MB; a 1200px y JPEG 85% queda en unos 100 KB. Sin
 * esto, cargar el catalogo desde el telefono en 4G es inviable, y el backend
 * tendria que traerse `sharp`, que es una dependencia nativa que hay que
 * compilar en el deploy.
 *
 * Todo lo que toca el DOM esta aca adentro y nada mas; el resto del modulo es
 * puro y se prueba sin navegador.
 */
export async function achicarImagen(archivo: File, lado = LADO_PRODUCTO): Promise<ImagenAchicada> {
  if (!archivo.type.startsWith("image/")) {
    throw new Error("Eso no es una imagen. Elegí un JPG, PNG o WEBP.");
  }

  const url = URL.createObjectURL(archivo);
  try {
    const img = await new Promise<HTMLImageElement>((resolver, rechazar) => {
      const el = new Image();
      el.onload = () => resolver(el);
      el.onerror = () => rechazar(new Error("No pudimos abrir esa imagen."));
      el.src = url;
    });

    const { ancho, alto } = medidasAchicadas(img.naturalWidth, img.naturalHeight, lado);
    const canvas = document.createElement("canvas");
    canvas.width = ancho;
    canvas.height = alto;

    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No pudimos procesar esa imagen.");
    ctx.drawImage(img, 0, 0, ancho, alto);

    const blob = await new Promise<Blob | null>((resolver) => {
      canvas.toBlob(resolver, "image/jpeg", 0.85);
    });
    if (!blob) throw new Error("No pudimos procesar esa imagen.");

    return { blob, ancho, alto };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** El multipart que esperan `PUT /productos/:id/imagen` y `PUT /config/logo`. */
export function formularioDeImagen(img: ImagenAchicada): FormData {
  const cuerpo = new FormData();
  cuerpo.append("archivo", new File([img.blob], "foto.jpg", { type: "image/jpeg" }));
  cuerpo.append("ancho", String(img.ancho));
  cuerpo.append("alto", String(img.alto));
  return cuerpo;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cual usado es cual
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La linea chica que separa un usado de otro: «Deep Blue · batería 89 % · en
 * el local · P560».
 *
 * En «Qué se publica» habia cuatro renglones «IPHONE 17 PRO MAX 256 GB
 * (USADO)» identicos salvo el precio —uno a pedido, dos en el local, otro
 * Silver— y en el dialogo de Destacados, ademas, sin la palabra «usado». Se
 * publicaba sin saber cual. Solo entra lo cargado; lo que siempre esta es
 * donde esta: en el local, a pedido (un renglon de la lista, sin IMEI) o, una
 * unidad que ya no esta, sin stock.
 */
export function detalleDeUsado(u: {
  color: string | null;
  bateria_pct: number | null;
  enLocal: boolean;
  esUnidad: boolean;
  codigo?: string | null;
}): string {
  const partes: string[] = [];
  if (u.color?.trim()) partes.push(colorDeIphone(u.color)?.etiqueta ?? u.color.trim());
  if (u.bateria_pct !== null) partes.push(`batería ${u.bateria_pct} %`);
  // v_catalogo_publico (0095, 0130) deja afuera a todo usado sin stock, sea
  // equipo o renglon. Antes aca decia «a pedido» a secas y el dueño tildaba
  // «Se ve» creyendo que el cliente lo iba a ver a pedido: no lo veia nunca.
  partes.push(
    u.enLocal ? "en el local" : `${u.esUnidad ? "sin stock" : "a pedido"} · no se ve en la tienda`,
  );
  if (u.codigo) partes.push(u.codigo);
  return partes.join(" · ");
}

/**
 * detalleDeUsado de un producto del catalogo interno. Equipo o renglon se
 * decide con esUnidadUsada (bateria O IMEI), la misma regla que la base: antes
 * los paneles miraban solo el IMEI y un equipo con bateria medida y sin IMEI
 * —los del proveedor— vendido salia «a pedido» en vez de «sin stock».
 */
export function detalleDeProductoUsado(
  p: Pick<Producto, "color" | "bateria_pct" | "stock_actual" | "es_usado" | "imei">,
  codigo?: string | null,
): string {
  return detalleDeUsado({
    color: p.color,
    bateria_pct: p.bateria_pct,
    enLocal: aNumero(p.stock_actual) > 0,
    esUnidad: esUnidadUsada(p),
    codigo,
  });
}
